<template>
  <CustomDialog
    v-model="dialog"
    title="stamp_card"
    title-icon="fal fa-stamp"
    :width="'420px'"
    :footer="false"
    :close-btn="true"
    :body-padding-top="false"
  >
    <template #activator>
      <div class="stamp-loyalty-modal">
        <v-tabs v-model="stampTab" grow class="stamp-tabs">
          <v-tab>{{ $t("stamp_tab_current") }}</v-tab>
          <v-tab>{{ $t("stamp_tab_history") }}</v-tab>
        </v-tabs>
        <v-tabs-items v-model="stampTab" class="stamp-tab-items">
          <v-tab-item>
            <div v-if="loading" class="stamp-empty-state">
              <v-progress-circular indeterminate color="primary" />
            </div>
            <template v-else-if="stampStatus">
              <div
                v-if="!stampStatus.stamp_feature_available"
                class="stamp-message-card"
              >
                <v-icon color="amber darken-2" class="mb-2">
                  fal fa-info-circle
                </v-icon>
                {{ $t("stamp_retail_only") }}
              </div>
              <div
                v-else-if="!stampStatus.is_active"
                class="stamp-message-card"
              >
                <v-icon color="amber darken-2" class="mb-2">
                  fal fa-pause-circle
                </v-icon>
                {{ $t("stamp_program_inactive") }}
              </div>
              <template v-else>
                <StampCard :stamp-status="stampStatus" />
                <v-alert
                  v-if="showRedeemValidation"
                  :type="redeemValidation.valid ? 'success' : 'info'"
                  dense
                  outlined
                  class="stamp-validation-alert"
                >
                  {{ $t(redeemValidation.key) }}
                </v-alert>
                <template v-if="stampStatus.eligible">
                  <v-alert
                    v-if="!applied && cartEmpty"
                    type="info"
                    dense
                    outlined
                    class="stamp-validation-alert"
                  >
                    {{ $t("stamp_pick_no_items") }}
                  </v-alert>
                  <v-btn
                    v-if="!applied"
                    color="primary"
                    block
                    depressed
                    class="stamp-action-btn"
                    :loading="applyLoading"
                    :disabled="applyDisabled || cartEmpty"
                    @click="$emit('apply-reward')"
                  >
                    <v-icon left small>fal fa-stamp</v-icon>
                    {{ $t("stamp_pick_free_item_button") }}
                  </v-btn>
                  <v-btn
                    v-else
                    color="orange darken-2"
                    block
                    outlined
                    class="stamp-action-btn"
                    :loading="applyLoading"
                    :disabled="applyDisabled"
                    @click="$emit('remove-reward')"
                  >
                    <v-icon left small>fal fa-times-circle</v-icon>
                    {{ $t("stamp_remove_reward") }}
                  </v-btn>
                </template>
              </template>
            </template>
            <div v-else class="stamp-message-card">
              <v-icon color="grey" class="mb-2">fal fa-stamp</v-icon>
              {{ $t("stamp_no_status") }}
            </div>
          </v-tab-item>
          <v-tab-item>
            <div v-if="!customerId" class="stamp-empty-state">
              <v-icon color="grey lighten-1" class="mb-2">fal fa-user</v-icon>
              {{ $t("stamp_history_no_customer") }}
            </div>
            <div v-else-if="historyLoading" class="stamp-empty-state">
              <v-progress-circular indeterminate color="primary" />
            </div>
            <v-alert
              v-else-if="historyFetchFailed"
              type="error"
              dense
              outlined
              class="mb-0"
            >
              {{ $t("stamp_history_load_error") }}
            </v-alert>
            <div v-else-if="!historyRows.length" class="stamp-empty-state">
              <v-icon color="grey lighten-1" class="mb-2">
                fal fa-history
              </v-icon>
              {{ $t("stamp_history_empty") }}
            </div>
            <v-list v-else dense two-line class="stamp-history-list">
              <v-list-item
                v-for="row in historyRows"
                :key="row.id"
                class="stamp-history-item"
              >
                <v-list-item-icon class="stamp-history-icon">
                  <v-icon
                    small
                    :color="
                      row.type === 'redeemed' ? 'orange darken-2' : 'primary'
                    "
                  >
                    fal fa-stamp
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{
                    historyRowTitle(row)
                  }}</v-list-item-title>
                  <v-list-item-subtitle>{{
                    formatHistoryDate(row.created_at)
                  }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-tab-item>
        </v-tabs-items>
      </div>
    </template>
  </CustomDialog>
</template>

<script>
import CustomDialog from "@/components/customDialog.vue";
import StampCard from "@/components/stamp/StampCard.vue";

export default {
  name: "StampLoyaltyModal",
  components: { CustomDialog, StampCard },
  props: {
    value: { type: Boolean, default: false },
    customerId: { type: [Number, String], default: null },
    stampStatus: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    applyLoading: { type: Boolean, default: false },
    applyDisabled: { type: Boolean, default: false },
    redeemValidation: { type: Object, default: null },
    applied: { type: Boolean, default: false },
    cartEmpty: { type: Boolean, default: false },
  },
  data() {
    return {
      stampTab: 0,
      historyRows: [],
      historyLoading: false,
      historyFetchFailed: false,
    };
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
    showRedeemValidation() {
      return !!(
        this.redeemValidation &&
        this.redeemValidation.key &&
        this.redeemValidation.key !== "stamp_not_enough_to_redeem"
      );
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.stampTab = 0;
        this.historyRows = [];
        this.historyFetchFailed = false;
      }
    },
    stampTab(val) {
      if (val === 1 && this.customerId) {
        this.fetchStampHistory();
      }
    },
    customerId() {
      this.historyRows = [];
      this.historyFetchFailed = false;
    },
  },
  methods: {
    async fetchStampHistory() {
      if (!this.customerId || this.historyLoading) return;
      this.historyLoading = true;
      this.historyFetchFailed = false;
      try {
        const { data } = await this.$http.get(
          `customers/${this.customerId}/stamp_history`,
          {
            params: { limit: 30 },
          },
        );
        if (data && data.status) {
          this.historyRows = Array.isArray(data.data) ? data.data : [];
        } else {
          this.historyRows = [];
          this.historyFetchFailed = true;
        }
      } catch (e) {
        this.historyRows = [];
        this.historyFetchFailed = true;
      } finally {
        this.historyLoading = false;
      }
    },
    historyRowTitle(row) {
      const n = Math.abs(Number(row.stamps_changed) || 0);
      if (row.type === "redeemed") {
        return this.$t("stamp_log_redeemed", { n });
      }
      return this.$t("stamp_log_earned", { n });
    },
    formatHistoryDate(iso) {
      if (!iso) return "";
      try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return String(iso);
        return d.toLocaleString();
      } catch (e) {
        return String(iso);
      }
    },
  },
};
</script>

<style scoped>
.stamp-loyalty-modal {
  --stamp-primary: #5b39c9;
  --stamp-primary-dark: #4527a8;
  --stamp-amber: #f59e0b;
  --stamp-ink: #0f172a;
  --stamp-muted: #64748b;
  --stamp-border: rgba(91, 57, 201, 0.12);
  padding: 0;
}

.stamp-tabs {
  margin-bottom: 18px;
  padding: 0 10px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}

.stamp-tabs ::v-deep .v-tab {
  min-height: 48px;
  color: var(--stamp-muted);
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.stamp-tabs ::v-deep .v-tab--active {
  color: var(--stamp-primary);
}

.stamp-tabs ::v-deep .v-tabs-slider {
  height: 3px;
  border-radius: 999px 999px 0 0;
  background: linear-gradient(90deg, var(--stamp-primary), var(--stamp-amber));
}

.stamp-tab-items {
  background: transparent;
}


.stamp-validation-alert {
  margin: 14px 0 0 !important;
  border-radius: 12px !important;
}

.stamp-action-btn {
  min-height: 44px;
  margin-top: 14px;
  border-radius: 12px !important;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: none;
  box-shadow: 0 12px 24px rgba(91, 57, 201, 0.18) !important;
}

.stamp-message-card,
.stamp-empty-state {
  display: flex;
  min-height: 150px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 22px;
  border: 1px dashed rgba(91, 57, 201, 0.18);
  border-radius: 16px;
  background: #fafafa;
  color: var(--stamp-muted);
  text-align: center;
  line-height: 1.6;
}

.stamp-history-list {
  padding: 2px 0 !important;
  background: transparent;
}

.stamp-history-item {
  min-height: 58px;
  margin-bottom: 8px;
  padding: 10px 12px !important;
  border: 1px solid rgba(15, 23, 42, 0.07);
  border-radius: 14px;
  background: #ffffff;
}

.stamp-history-icon {
  display: inline-flex;
  align-self: center;
  justify-content: center;
  width: 34px;
  min-width: 34px;
  height: 34px;
  margin: 0 12px 0 0 !important;
  border-radius: 10px;
  background: rgba(91, 57, 201, 0.08);
}

@media (max-width: 600px) {
  .stamp-loyalty-modal {
    padding: 0 16px 18px;
  }

  .stamp-tabs {
    margin-right: -16px;
    margin-left: -16px;
  }
}
</style>
